import type { UserInfo } from 'matomo-tracker-react-native';
import MatomoTracker from 'matomo-tracker-react-native';
import { create } from 'zustand';

export type AnalyticsEventParams = {
  action: string;
  name?: string;
  category?: string;
  value?: number;
  campaign?: string;
  userInfo?: UserInfo;
};

export type AnalyticsPageViewParams = {
  name: string;
  url?: string;
  userInfo?: UserInfo;
};

export type AnalyticsState = {
  isInitialized: boolean;
  isEnabled: boolean;
  siteId?: number;
  urlBase?: string;
  trackerUrl?: string;
  userId?: string;
  disabled?: boolean;
  log?: boolean;
  matomoInstance?: MatomoTracker;
  presetUserInfo?: UserInfo;
};

export type AnalyticsActions = {
  initialize: (config: {
    siteId: number;
    urlBase: string;
    trackerUrl?: string;
    userId?: string;
    disabled?: boolean;
    log?: boolean;
  }) => void;
  setEnabled: (enabled: boolean) => void;
  setPresetUserInfo: (userInfo: UserInfo) => void;
  clearPresetUserInfo: () => void;
  trackAppStart: (userInfo?: UserInfo) => Promise<void>;
  trackEvent: (params: AnalyticsEventParams) => Promise<void>;
  trackScreenView: (params: AnalyticsPageViewParams) => Promise<void>;
  trackAction: (params: AnalyticsPageViewParams) => Promise<void>;
  trackSiteSearch: (params: {
    keyword: string;
    category?: string;
    count?: number;
    userInfo?: UserInfo;
  }) => Promise<void>;
  trackLink: (params: { link: string; userInfo?: UserInfo }) => Promise<void>;
  trackDownload: (params: { download: string; userInfo?: UserInfo }) => Promise<void>;
  setUserId: (userId: string | null) => void;
  setCustomDimension: (id: number, value: string) => void;
  reset: () => void;
};

export type AnalyticsStore = AnalyticsState & AnalyticsActions;

export const useAnalyticsStore = create<AnalyticsStore>((set, get) => ({
  isInitialized: false,
  isEnabled: true,
  siteId: undefined,
  urlBase: undefined,
  trackerUrl: undefined,
  userId: undefined,
  disabled: false,
  log: false,
  matomoInstance: undefined,
  presetUserInfo: undefined,

  initialize: (config) => {
    try {
      // Create Matomo tracker instance
      const matomoInstance = new MatomoTracker({
        urlBase: config.urlBase,
        siteId: config.siteId,
        trackerUrl: config.trackerUrl,
        userId: config.userId,
        disabled: config.disabled || false,
        log: config.log || false,
      });

      set({
        siteId: config.siteId,
        urlBase: config.urlBase,
        trackerUrl: config.trackerUrl,
        userId: config.userId,
        disabled: config.disabled || false,
        log: config.log || false,
        matomoInstance,
        isInitialized: true,
      });

      console.log('Analytics: Matomo tracker initialized', config);
    } catch (error) {
      console.error('Analytics: Failed to initialize Matomo tracker', error);
      set({
        siteId: config.siteId,
        urlBase: config.urlBase,
        trackerUrl: config.trackerUrl,
        userId: config.userId,
        disabled: config.disabled || false,
        log: config.log || false,
        isInitialized: false,
      });
    }
  },

  setEnabled: (enabled: boolean) => {
    set({ isEnabled: enabled });
  },

  setPresetUserInfo: (userInfo: UserInfo) => {
    set({ presetUserInfo: userInfo });
  },

  clearPresetUserInfo: () => {
    set({ presetUserInfo: undefined });
  },

  trackAppStart: async (userInfo?: UserInfo) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track App Start skipped - not initialized, disabled, or tracking disabled');
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...userInfo };

      await state.matomoInstance.trackAppStart({
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: App start tracked successfully', { userInfo: mergedUserInfo });
      }
    } catch (error) {
      console.error('Analytics: Failed to track app start', error);
    }
  },

  trackEvent: async (params: AnalyticsEventParams) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track Event skipped - not initialized, disabled, or tracking disabled', params);
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...params.userInfo };

      await state.matomoInstance.trackEvent({
        category: params.category || 'User Action',
        action: params.action,
        name: params.name,
        value: params.value,
        campaign: params.campaign,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: Event tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track event', error, params);
    }
  },

  trackScreenView: async (params: AnalyticsPageViewParams) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track Screen View skipped - not initialized, disabled, or tracking disabled', params);
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...params.userInfo };

      await state.matomoInstance.trackScreenView({
        name: params.name,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: Screen view tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track screen view', error, params);
    }
  },

  trackAction: async (params: AnalyticsPageViewParams) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track Action skipped - not initialized, disabled, or tracking disabled', params);
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...params.userInfo };

      await state.matomoInstance.trackAction({
        name: params.name,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: Action tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track action', error, params);
    }
  },

  trackSiteSearch: async (params: { keyword: string; category?: string; count?: number; userInfo?: UserInfo }) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track Site Search skipped - not initialized, disabled, or tracking disabled', params);
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...params.userInfo };

      await state.matomoInstance.trackSiteSearch({
        keyword: params.keyword,
        category: params.category,
        count: params.count,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: Site search tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track site search', error, params);
    }
  },

  trackLink: async (params: { link: string; userInfo?: UserInfo }) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track Link skipped - not initialized, disabled, or tracking disabled', params);
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...params.userInfo };

      await state.matomoInstance.trackLink({
        link: params.link,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: Link tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track link', error, params);
    }
  },

  trackDownload: async (params: { download: string; userInfo?: UserInfo }) => {
    const state = get();
    if (!state.isInitialized || !state.isEnabled || !state.matomoInstance || state.disabled) {
      console.debug('Analytics: Track Download skipped - not initialized, disabled, or tracking disabled', params);
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...state.presetUserInfo, ...params.userInfo };

      await state.matomoInstance.trackDownload({
        download: params.download,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (state.log) {
        console.debug('Analytics: Download tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track download', error, params);
    }
  },

  setUserId: (userId: string | null) => {
    const state = get();

    // Update both the userId state and the preset userInfo
    if (userId) {
      const updatedUserInfo = {
        ...state.presetUserInfo,
        uid: userId,
      };
      set({
        userId: userId,
        presetUserInfo: updatedUserInfo,
      });
    } else {
      // Remove uid from preset userInfo if userId is null
      const currentUserInfo = state.presetUserInfo || {};
      if (currentUserInfo.uid) {
        const updatedUserInfo = { ...currentUserInfo };
        delete updatedUserInfo.uid;

        set({
          userId: undefined,
          presetUserInfo: Object.keys(updatedUserInfo).length > 0 ? updatedUserInfo : undefined,
        });
      } else {
        set({ userId: undefined });
      }
    }

    if (state.log) {
      console.debug('Analytics: User ID updated in preset userInfo', userId);
    }
  },

  setCustomDimension: (id: number, value: string) => {
    const state = get();
    if (!state.presetUserInfo) {
      set({ presetUserInfo: { [`dimension${id}`]: value } });
    } else {
      set({
        presetUserInfo: {
          ...state.presetUserInfo,
          [`dimension${id}`]: value,
        },
      });
    }

    if (state.log) {
      console.debug('Analytics: Custom dimension set in preset userInfo', { id, value });
    }
  },

  reset: () => {
    set({
      isInitialized: false,
      isEnabled: true,
      siteId: undefined,
      urlBase: undefined,
      trackerUrl: undefined,
      userId: undefined,
      disabled: false,
      log: false,
      matomoInstance: undefined,
      presetUserInfo: undefined,
    });

    console.debug('Analytics: Store reset successfully');
  },
}));
